import { useShortcutActions } from 'app/_hooks'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Colors } from '~/libs/mui/color'
import { useLogger } from '../useLogger'
import { useVideoScale } from '../useVideoScale'
import { useVolume } from '../useVolume'
import { PlayerButton } from './PlayerButton'

type Props = {
  video: HTMLVideoElement
  flashIcon: (type: string) => void
}

export const Volume = ({ video, flashIcon }: Props) => {
  const { videoScale } = useVideoScale()
  const { mute: muteLog, unmute: unmuteLog, changeVolume: changeVolumeLog } = useLogger()
  const {
    volume: storageVolume,
    muted: storageMuted,
    updateVolume: updateStorageVolume,
    updateMute: updateStorageMute,
  } = useVolume()
  const [volumeBarRate, setVolumeBarRate] = useState(storageVolume)
  const barRef = useRef<HTMLDivElement>(null)
  const size = useMemo(() => videoScale * 52, [videoScale])

  // Initialize
  useEffect(() => {
    video.volume = storageVolume
    video.muted = storageMuted
    setVolumeBarRate(storageVolume)
  }, [video])

  const updateVolume = useCallback(
    (volume: number) => {
      video.volume = volume
      setVolumeBarRate(volume)
      updateStorageVolume(volume)
    },
    [video],
  )

  const updateMute = useCallback(
    (muted: boolean) => {
      video.muted = muted
      updateStorageMute(muted)
    },
    [video],
  )

  const handleChangeVolume = useCallback(
    (clientX: number) => {
      if (!barRef.current) return

      const rect = barRef.current.getBoundingClientRect()
      const clickX = clientX - rect.left
      const ratio = Math.min(Math.max(clickX / rect.width, 0), 1)

      updateMute(false)
      updateVolume(ratio)
      changeVolumeLog()
    },
    [updateMute, updateVolume, changeVolumeLog],
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      handleChangeVolume(e.clientX)

      const handleMouseMove = (e: MouseEvent) => {
        handleChangeVolume(e.clientX)
      }

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [handleChangeVolume],
  )

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      handleChangeVolume(e.clientX)
    },
    [handleChangeVolume],
  )

  const handleMute = useCallback(() => {
    updateMute(true)
    setVolumeBarRate(0)
    muteLog()
  }, [updateMute, muteLog])

  const handleUnmute = useCallback(() => {
    updateMute(false)

    updateVolume(storageVolume)
    unmuteLog()
  }, [updateMute, updateVolume, unmuteLog, storageVolume])

  const shortcutActions = useMemo(
    () => ({
      KeyM: () => {
        if (volumeBarRate === 0) {
          handleUnmute()
          flashIcon('sound_on')
        } else {
          handleMute()
          flashIcon('sound_off')
        }
      },
      ArrowUp: () => {
        if (video.volume < 1) {
          updateVolume(Math.min(video.volume + 0.1, 1))
          flashIcon('sound_on')
        }
      },
      ArrowDown: () => {
        if (video.volume > 0) {
          updateVolume(Math.max(video.volume - 0.1, 0))
          if (video.volume === 0) {
            flashIcon('sound_off')
          } else {
            flashIcon('sound_on')
          }
        }
      },
    }),
    [video, volumeBarRate, handleMute, handleUnmute, flashIcon],
  )
  useShortcutActions(shortcutActions)

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: `${videoScale * 24}px`,
      }}
    >
      {volumeBarRate === 0 ? (
        <PlayerButton onClick={handleUnmute}>
          <Image src='/player/sound_off.png' alt='Mute' width={size} height={size} />
        </PlayerButton>
      ) : (
        <PlayerButton onClick={handleMute}>
          <Image src='/player/sound_on.png' alt='Volume' width={size} height={size} />
        </PlayerButton>
      )}
      <div
        ref={barRef}
        style={{
          width: `${videoScale * 120}px`,
          height: size,
          position: 'relative',
          cursor: 'pointer',
        }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            transform: 'translateY(-50%)',
            width: '100%',
            height: `${videoScale * 4}px`,
            backgroundColor: Colors.color7E7E80,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            transformOrigin: 'left',
            transform: `translateY(-50%)`,
            width: `${volumeBarRate * 100}%`,
            height: `${videoScale * 4}px`,
            backgroundColor: 'white',
          }}
        />
      </div>
    </div>
  )
}
